/* oxlint-disable no-param-reassign -- https://redux-toolkit.js.org/usage/immer-reducers  */
import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { z } from 'zod';
import { draftClaimLineItemSchema } from '~/utils/schemas';
import {
  completeDraftClaimLineItem,
  invalidateLineItem,
  reset,
} from './sharedActions';

export const draftClaimLineItemStateSchema = z.nullable(
  draftClaimLineItemSchema.extend({
    /*
     * with the policy enforcement and current architecture there is a need to preserve the state but make it as invalid
     * so the policy page, layout can still access it while the middleware can determine when to redirect for invalid state
     */
    invalid: z.boolean().optional(),
  }),
);

export type DraftClaimLineItemState = z.infer<
  typeof draftClaimLineItemStateSchema
>;

function assertInitialized(
  state: Draft<DraftClaimLineItemState>,
): asserts state is Draft<NonNullable<DraftClaimLineItemState>> {
  if (state === null) {
    throw new Error('Draft claim line item not initialized');
  }
}

export const draftClaimLineItemSlice = createSlice({
  name: 'draftClaimLineItem',
  initialState: null as DraftClaimLineItemState,
  reducers: {
    startNewClaim: (
      state,
      action: PayloadAction<z.infer<typeof draftClaimLineItemSchema>>,
    ) => action.payload,
    clear: () => null,
    setQuantity: (
      state,
      action: PayloadAction<NonNullable<DraftClaimLineItemState>['quantity']>,
    ) => {
      assertInitialized(state);

      state.quantity = action.payload;
    },
    setReason: (
      state,
      action: PayloadAction<
        Required<
          Pick<
            NonNullable<DraftClaimLineItemState>,
            'reasonId' | 'reasonCategoryCode'
          >
        >
      >,
    ) => {
      assertInitialized(state);

      const { reasonId, reasonCategoryCode } = action.payload;

      if (reasonId !== state.reasonId) {
        state.reasonDetailId = undefined; // clear out any previous reason detail
        state.reasonPolicy = undefined; // clear out any previous reason policy
        state.reasonCustomFields = undefined; // clear out any previous reason custom fields
        state.omittedResolutionMethods = undefined; // omissions are derived from the reason
        state.customFields = {}; // clear out any previous custom fields that are not part of the new reason custom fields
      }

      state.reasonId = reasonId;
      state.reasonCategoryCode = reasonCategoryCode;
    },
    setReasonDetail: (
      state,
      action: PayloadAction<
        NonNullable<NonNullable<DraftClaimLineItemState>['reasonDetailId']>
      >,
    ) => {
      assertInitialized(state);

      const reasonDetailId = action.payload;

      if (reasonDetailId !== state.reasonDetailId) {
        state.reasonPolicy = undefined; // clear out any previous reason policy
        state.reasonCustomFields = undefined; // clear out any previous reason custom fields
        state.omittedResolutionMethods = undefined; // omissions are derived from the reason
        state.customFields = {}; // clear out any previous custom fields that are not part of the new reason custom fields
      }

      state.reasonDetailId = reasonDetailId;
    },
    setReasonPolicy: (
      state,
      action: PayloadAction<
        NonNullable<DraftClaimLineItemState>['reasonPolicy']
      >,
    ) => {
      assertInitialized(state);

      state.reasonPolicy = action.payload;
    },
    setOmittedResolutionMethods: (
      state,
      action: PayloadAction<
        NonNullable<DraftClaimLineItemState>['omittedResolutionMethods']
      >,
    ) => {
      assertInitialized(state);

      state.omittedResolutionMethods = action.payload;
    },
    setReasonCustomFields: (
      state,
      action: PayloadAction<
        NonNullable<DraftClaimLineItemState>['reasonCustomFields']
      >,
    ) => {
      assertInitialized(state);

      state.reasonCustomFields = action.payload;
      // clear out any previous custom fields that are not part of the new reason custom fields
      state.customFields = {};
    },
    setComments: (
      state,
      action: PayloadAction<NonNullable<DraftClaimLineItemState>['comments']>,
    ) => {
      assertInitialized(state);
      state.comments = action.payload;
    },
    setMeta: (
      state,
      action: PayloadAction<
        Pick<
          NonNullable<DraftClaimLineItemState>,
          'assets' | 'comments' | 'customFields'
        >
      >,
    ) => {
      assertInitialized(state);

      state.assets = action.payload.assets;
      state.comments = action.payload.comments;
      state.customFields = action.payload.customFields;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(invalidateLineItem, (state) => {
        assertInitialized(state);
        state.invalid = true;
      })
      .addCase(completeDraftClaimLineItem, () => null)
      .addCase(reset, () => null);
  },
});
